'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import type { Form } from '../../payload-types';
import type { BookingMode, BookingPage } from '../../utilities/forms/booking-page';
import { loadCalendly, openCalendlyPopup } from '../../utilities/calendly/load-calendly';
import { trackClick } from '../../utilities/track-click';
import { useAnalyticsTenantId } from './analytics-context';

// Lazy-load the tracker so `react-calendly` only ships when a page actually
// has a booking Form. Otherwise this entire dependency would be pulled into
// the shared client chunk on every tenant page (the navbar renders form
// buttons globally).
const CalendlyTracker = dynamic(() => import('./calendly-tracker'), { ssr: false });

// Calendly script load failures are recoverable: the visitor can click again
// once connectivity is restored. Swallow rejections so they don't surface
// as `unhandledrejection` and pollute error telemetry.
const swallowLoaderRejection = (_reason: unknown): void => undefined;

// Compile-time guard: the button opens Calendly's popup and nothing else, so
// a second booking mode fails to compile here until the button opens that
// mode's Booking Page too.
const _onlyCalendlyOpensFromTheButton: Exclude<BookingMode, 'calendly'> extends never ? true : false = true;

/** What the button reads when its placement sets no text. */
export const BOOKING_BUTTON_FALLBACK_TEXT = 'Book a time';

export type BookingButtonProps = {
  form: Form;
  bookingPage: BookingPage;
  /**
   * The button's text, and the label its click event records. Empty or unset,
   * the button reads `BOOKING_BUTTON_FALLBACK_TEXT` and records that.
   */
  text?: string | null;
  /**
   * Server-rendered label for `text` (with prepend/append icons), drawn in
   * place of it. Ignored when `text` is empty or unset, since it would only
   * draw that empty text.
   */
  label?: React.ReactNode;
  className: string;
  /** Called as the booking opens, before the Booking Page shows. */
  onClick?: () => void;
};

/**
 * The button a booking Form shows wherever a field Form would open its modal
 * or draw its fields: it opens the Form's Booking Page. Rendered as a plain
 * `type="button"` so the server HTML contains it (no flicker after
 * hydration) and so the phone menu closes itself over the popup
 * (`mobile-drawer.tsx` leaves submit buttons alone).
 *
 * Opening records the same `form_button` click the field modal records.
 */
export const BookingButton: React.FC<BookingButtonProps> = ({
  form,
  bookingPage,
  text,
  label,
  className,
  onClick,
}) => {
  const analyticsTenantId = useAnalyticsTenantId();
  const buttonText = text || BOOKING_BUTTON_FALLBACK_TEXT;
  const shown = text ? (label ?? text) : BOOKING_BUTTON_FALLBACK_TEXT;

  const open = () => {
    if (analyticsTenantId) {
      trackClick(analyticsTenantId, 'form_button', form.name ?? form.id, {
        label: buttonText,
        formName: form.name ?? undefined,
      });
    }
    onClick?.();
    openCalendlyPopup(bookingPage.url, form.id).catch(swallowLoaderRejection);
  };

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={open}
        onMouseEnter={() => { loadCalendly().catch(swallowLoaderRejection); }}
        onFocus={() => { loadCalendly().catch(swallowLoaderRejection); }}
      >
        {shown}
      </button>
      <CalendlyTracker form={form} />
    </>
  );
};
